import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Fab,
  Typography,
} from "@mui/material";
import FilterableSkuList from "components/FilterableSkuList";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ISaveProduct, SaveProduct } from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import ProductForm from "./ProductForm";
import ProductHandouts from "./ProductHandouts";
import useTitle from "hooks/useTitle";
import contextStore from "stores/contextStore";

export default function ProductUpdatePage() {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    skuCreateUrl,
    productsUrl,
    productService,
    productDetailsUrl,
  } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { organizationId } = contextStore.useState();
  const { isLoading, isError, data } = useQuery(
    ["product", id],
    () => productService.getProductById(id!),
    {
      enabled: !!id,
    }
  );

  const product = data?.data;
  const getFirstNonEmpty = (...fields: (string | null | undefined)[]) =>
    fields.find((f) => !!f && f.trim() !== "");
  const productTitle =
    getFirstNonEmpty(product?.name, product?.value, product?.title) ||
    t({ id: "productDetails" });

  useTitle(productTitle);

  if (!id) return <Navigate to={productsUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="productLoadError" />
        </Alert>
      </Box>
    );
  }

  const onSubmit = async (values: ISaveProduct) => {
    try {
      await productService.saveProduct(
        new SaveProduct({ ...values, id: product!.id })
      );

      queryClient.invalidateQueries("products");
      queryClient.invalidateQueries(["product", id]);
      setSnackbarMessage(new SnackbarMessageProps(t({ id: "productUpdated" })));
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={productDetailsUrl(id)}
          labelKey="backToDetailsButtonLabel"
        />
      </Box>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="productDetails" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProductForm
            product={product}
            onSubmit={onSubmit}
            submitLabel={t({ id: "productFormUpdateButton" })}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="productRelatedSkusLabel" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FilterableSkuList
            organizationId={organizationId}
            preSelectedProductId={id}
            lazyTableTitle=""
          />

          <Box mt={3} textAlign="center">
            <Fab
              aria-label="add"
              color="primary"
              onClick={() => navigate(skuCreateUrl(id))}
            >
              <AddIcon />
            </Fab>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="courseHandouts" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProductHandouts productId={id} />
        </AccordionDetails>
      </Accordion>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
}
